// TableContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import axios from "axios";
import {
  API_KEY,
  API_TOKEN,
  FetchApi,
  FetchApiNoParam,
} from "../../../utils/api";

interface TimeSlot {
  time_slot: string;
  Table_left: number;
  Deposit: number;
}

interface Table {
  name?: string;
  timeSlots?: TimeSlot[];
  // add other fields returned by API
}

interface TableBooking {
  order_id: string;
  store_id: string;
  store_name: string;
  datereserve: string;
  timereserve: string;
  number_adult: string;
  number_child: string;
  table_id: string;
  payment_amount: string;
  payment_link: string;
  create_time: string;
  remark: string;
  dining_duration: string;
  status: string;
  payment_status: string;
  payment_ref: string;
}

interface CurrentBooking {
  selectedProduct?: Table;
  bookingParams?: {
    store_id: number;
    datereserve: string;
    timereserve: TimeSlot;
    number_adult: number;
    number_child: number;
    name: string;
    phone: string;
    email: string;
  };
}

interface CreateBookingResponse {
  status: number;
  payment_redirect_url?: string;
  order_number?: string;
  message?: string;
}

interface TableContextType {
  table: Table;
  tableBookings: TableBooking[];
  loading: boolean;
  error: string | null;
  bookingLoading: boolean;
  bookingError: any;
  fetchTable: (parameters: {
    name?: string;
    date?: string;
    voucher_code?: string;
    adultPax?: number;
    childPax?: number;
  }) => Promise<Table[]>;
  createTableBooking: (params: any) => Promise<CreateBookingResponse>;
  currentBooking: CurrentBooking;
  onCurrentBooking: (booking: any) => void;
  fetchTableBookings: (parameters: { UUID: string }) => Promise<TableBooking[]>;
  createVoucherTableBooking: (params: any) => Promise<CreateBookingResponse>;
}

const TableContext = createContext<TableContextType | undefined>(undefined);

export const TableProvider = ({ children }: { children: ReactNode }) => {
  const [table, setTable] = useState<Table>({});
  const [tableBookings, setTableBookings] = useState<TableBooking[]>([]);
  const [currentBooking, setCurrentBooking] = useState<CurrentBooking>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<boolean>(false);
  const [bookingError, setBookingError] = useState<any>(null);

  const onCurrentBooking = (booking: any) => setCurrentBooking(booking);

  // In TableContext.tsx - update the createVoucherTableBooking function
  const createVoucherTableBooking = useCallback(
    async (parameters: any): Promise<CreateBookingResponse> => {
      setBookingLoading(true);
      setBookingError(null);
      try {
        const url = FetchApiNoParam("create_voucher_table_booking.php");
        console.log("check url createVoucherTableBooking ===> ", url);

        // Create FormData object for POST request
        const formData = new FormData();

        // Append basic booking parameters
        formData.append("apikey", API_KEY);
        formData.append("apitoken", API_TOKEN);
        formData.append("store_id", parameters.store_id);
        formData.append("datereserve", parameters.datereserve);
        formData.append("timereserve", parameters.timereserve);
        formData.append("number_adult", parameters.number_adult);
        formData.append("number_child", parameters.number_child);
        formData.append("name", parameters.name);
        formData.append("email", parameters.email);
        formData.append("phone", parameters.phone);
        formData.append("promotion_code", parameters.promotion_code || "");
        formData.append("referral", parameters.referral || "");
        formData.append("voucher_ticket", "1");

        // Append voucher_ids as array
        if (parameters.voucher_ids && parameters.voucher_ids.length > 0) {
          parameters.voucher_ids.forEach((voucherId: string, index: number) => {
            formData.append(`voucher_ids[${index}]`, voucherId);
          });
        }

        // Log FormData contents for debugging
        console.log("FormData contents for voucher table booking:");
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        const response = await axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("check response createVoucherTableBooking ===> ", response);

        if (response.data.status === 1) {
          return response.data;
        }
        throw new Error(
          response.data.message || "Failed to create voucher table booking"
        );
      } catch (err: any) {
        console.error("Voucher table booking creation error:", err);
        setBookingError(err.response?.data || err.message || err);
        throw err;
      } finally {
        setBookingLoading(false);
      }
    },
    []
  );

  const createTableBooking = useCallback(
    async (parameters: any): Promise<CreateBookingResponse> => {
      setBookingLoading(true);
      setBookingError(null);
      try {
        const url = FetchApi("create_table_booking.php", parameters);
        console.log("check url createTableBooking ===> ", url);
        const response = await axios.get(url);
        console.log("check response createTableBooking ===> ", response);

        if (response.data.status === 1) {
          return response.data;
        }
        throw new Error(response.data.message || "Failed to create booking");
      } catch (err: any) {
        console.error("Booking creation error:", err);
        setBookingError(err.response?.data || err.message || err);
        throw err;
      } finally {
        setBookingLoading(false);
      }
    },
    []
  );

  const fetchTable = async (parameters: {
    name?: string;
    date?: string;
    voucher_code?: string;
    adultPax?: number;
    childPax?: number;
  }): Promise<Table[]> => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("table_search.php", {
        name: parameters?.name,
        date: parameters?.date,
        voucher_code: parameters?.voucher_code || "",
        adultPax: parameters?.adultPax,
        childPax: parameters?.childPax,
      });
      console.log("URL ======= fetchTable with vouchers", url);

      const response = await axios.get(url);

      if (response.data) {
        console.log("Valid Response ======= fetchTable", response.data);
        setTable(response.data);
        return response.data;
      } else {
        console.log("Invalid Response ======= fetchTable", response.data);
        const errorMsg = "Invalid response from server";
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.log("Catch Error ======= fetchTable", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Unknown error occurred";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchTableBookings = async (parameters: {
    UUID: string;
  }): Promise<TableBooking[]> => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("list_user_table_booking.php", parameters);
      console.log("URL ======= fetchTableBookings", url);
      const response = await axios.get(url);

      if (response.data) {
        console.log("Valid Response ======= fetchTableBookings", response.data);

        // Ensure we always set an array, even if response is empty
        const bookings = Array.isArray(response.data) ? response.data : [];
        setTableBookings(bookings);
        return bookings;
      } else {
        console.log(
          "Invalid Response ======= fetchTableBookings",
          response.data
        );
        const errorMsg = "Invalid response from server";
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.log("Catch Error ======= fetchTableBookings", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch bookings";
      setError(errorMsg);

      // Set empty array on error to prevent crashes
      setTableBookings([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value: TableContextType = {
    table,
    tableBookings,
    currentBooking,
    loading,
    error,
    bookingLoading,
    bookingError,
    fetchTable,
    fetchTableBookings,
    onCurrentBooking,
    createTableBooking,
    createVoucherTableBooking,
  };

  return (
    <TableContext.Provider value={value}>{children}</TableContext.Provider>
  );
};

export const useTable = () => {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error("useTable must be used within TableProvider");
  }
  return context;
};
